import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Area,
  ComposedChart,
} from 'recharts';

interface TimeSeriesChartProps {
  data: Array<{
    date: string;
    value: number;
    min?: number;
    max?: number;
  }>;
  title?: string;
  yAxisLabel?: string;
  color?: string;
  showArea?: boolean;
  showMinMax?: boolean;
}

export default function TimeSeriesChart({
  data,
  title,
  yAxisLabel,
  color = '#00d9ff',
  showArea = false,
  showMinMax = false,
}: TimeSeriesChartProps) {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-cyber-text-muted">
        No evolution data available
      </div>
    );
  }

  const ChartComponent = showMinMax ? ComposedChart : LineChart;

  return (
    <div className="w-full">
      {title && (
        <h3 className="text-lg font-display font-bold text-cyber-text-primary mb-4">
          {title}
        </h3>
      )}
      <ResponsiveContainer width="100%" height={300}>
        <ChartComponent data={data}>
          <defs>
            <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.3} />
              <stop offset="95%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#2a2a4a" />
          <XAxis
            dataKey="date"
            tick={{ fill: '#8b9dc3', fontSize: 11 }}
            stroke="#2a2a4a"
          />
          <YAxis
            label={{
              value: yAxisLabel || 'Value',
              angle: -90,
              position: 'insideLeft',
              fill: '#8b9dc3',
              fontSize: 12,
            }}
            tick={{ fill: '#8b9dc3', fontSize: 11 }}
            stroke="#2a2a4a"
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#0a0a1a',
              border: `1px solid ${color}`,
              borderRadius: '8px',
              color: '#dfe6e9',
            }}
            formatter={(value: number, name: string) => {
              if (name === 'value') return [value.toFixed(1), yAxisLabel || 'Value'];
              if (name === 'min') return [value.toFixed(1), 'Min'];
              if (name === 'max') return [value.toFixed(1), 'Max'];
              return [value, name];
            }}
          />
          <Legend wrapperStyle={{ color: '#dfe6e9' }} />

          {showArea && (
            <Area
              type="monotone"
              dataKey="value"
              stroke={color}
              fillOpacity={1}
              fill="url(#colorValue)"
            />
          )}

          {showMinMax && data[0]?.min !== undefined && (
            <>
              <Area
                type="monotone"
                dataKey="min"
                stroke="#666"
                fill="transparent"
                strokeDasharray="3 3"
              />
              <Area
                type="monotone"
                dataKey="max"
                stroke="#666"
                fill="transparent"
                strokeDasharray="3 3"
              />
            </>
          )}

          <Line
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={2}
            dot={{ fill: color, r: 4 }}
            activeDot={{ r: 6, fill: color }}
          />
        </ChartComponent>
      </ResponsiveContainer>
    </div>
  );
}
